import React, { useEffect, useRef, useState } from 'react';

const GESTURE_EVENTS = ['pointerup', 'touchend', 'click', 'keydown'];
const LISTENER_OPTS = { capture: true, passive: true };

const warn = (err) => console.warn('[videoSound]', err);

const SoundOnIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M11 5L6 9H2v6h4l5 4V5z" fill="currentColor" />
    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
  </svg>
);

const MutedIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M11 5L6 9H2v6h4l5 4V5z" fill="currentColor" />
    <line x1="23" y1="9" x2="17" y2="15" />
    <line x1="17" y1="9" x2="23" y2="15" />
  </svg>
);

export default function SoundVideo({ src, loop }) {
  const videoRef = useRef(null);
  const buttonRef = useRef(null);
  const userMutedRef = useRef(false);
  const autoMutedRef = useRef(false);
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    let cancelled = false;
    let timer;

    const warnIfActive = (err) => {
      if (!cancelled) warn(err);
    };

    const addListeners = () => {
      GESTURE_EVENTS.forEach((type) => document.addEventListener(type, onGesture, LISTENER_OPTS));
    };

    const removeListeners = () => {
      GESTURE_EVENTS.forEach((type) => document.removeEventListener(type, onGesture, LISTENER_OPTS));
    };

    const playMuted = () => {
      video.muted = true;
      autoMutedRef.current = true;
      video.play().catch(warnIfActive);
      addListeners();
    };

    const tryPlay = () => {
      video.muted = userMutedRef.current;
      video.play().catch((err) => {
        if (cancelled) return;
        // 사용자 활성화 전에는 소리 있는 자동재생이 차단됨 → 음소거로 재생 후 첫 제스처에 해제
        if (err.name === 'NotAllowedError' && !video.muted) playMuted();
        else if (err.name !== 'AbortError') warn(err);
      });
    };

    function onGesture(e) {
      if (buttonRef.current?.contains(e.target)) return;
      if (navigator.userActivation && !navigator.userActivation.isActive) return;
      if (userMutedRef.current || !autoMutedRef.current) {
        removeListeners();
        return;
      }
      // iOS Safari는 제스처 핸들러 안에서 동기적으로 해제해야 재생이 유지됨
      video.muted = false;
      autoMutedRef.current = false;
      if (video.paused) video.play().catch(warnIfActive);
      removeListeners();
      timer = setTimeout(() => {
        if (cancelled || userMutedRef.current) return;
        // 소리 해제가 거부되어 일시정지된 경우 음소거 재생으로 복귀
        if (video.paused && !(video.ended && !video.loop)) playMuted();
      }, 0);
    }

    const onVolumeChange = () => setMuted(video.muted);

    video.addEventListener('volumechange', onVolumeChange);
    tryPlay();

    return () => {
      cancelled = true;
      clearTimeout(timer);
      removeListeners();
      video.removeEventListener('volumechange', onVolumeChange);
    };
  }, [src]);

  const toggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const video = videoRef.current;
    if (video.muted) {
      userMutedRef.current = false;
      autoMutedRef.current = false;
      video.muted = false;
      if (video.paused) video.play().catch(warn);
    } else {
      userMutedRef.current = true;
      video.muted = true;
    }
  };

  return (
    <div className="zt-video-wrap">
      <video
        ref={videoRef}
        src={src}
        loop={loop}
        playsInline
        preload="auto"
        style={{ width: '100%', height: 'auto', display: 'block' }}
      >
        Your browser does not support the video tag.
      </video>
      <button
        ref={buttonRef}
        type="button"
        className="zt-video-sound-btn"
        aria-label={muted ? '소리 켜기' : '소리 끄기'}
        aria-pressed={muted}
        onClick={toggle}
      >
        {muted ? <MutedIcon /> : <SoundOnIcon />}
      </button>
    </div>
  );
}
